import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { getStartEndOfMonth } from "../../../../../utils/methods";
import { ManagerStatementListFilters } from "../../../../../interfaces/managerStatement.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  managers: any[];
  allManagers: any[];
  statuses: any[];
  allStatuses: any[];
  showManagerFilter: boolean;

  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ManagerStatementListFilters;
  onApply: (filters: ManagerStatementListFilters) => void;
  onReset: () => void;
}

const Filters: React.FC<FilterProps> = ({
  managers, allManagers,
  statuses, allStatuses,
  showManagerFilter,
  dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ManagerStatementListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  const isApplied =
    initialFilters.searchText !== '' ||
    initialFilters.managedBy !== '' ||
    initialFilters.status !== '' ||
    initialFilters.startDate !== defaultDates.startDate ||
    initialFilters.endDate !== defaultDates.endDate;

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText,
    };

    const applied =
      filtersToApply.searchText !== '' ||
      filtersToApply.managedBy !== '' ||
      filtersToApply.status !== '' ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate;

    setFiltersApplied(applied);
    onApply(filtersToApply);
  }, [
    debouncedSearchText,
    filters.managedBy, filters.status,
    filters.startDate, filters.endDate,
    defaultDates, onApply,
  ]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ManagerStatementListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ManagerStatementListFilters = {
      searchText: '',
      managedBy: '',
      status: '',
      ...getStartEndOfMonth(),
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3 mb-3">
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                max={filters.endDate}
                placeholder={t('reconciliation.managerStatement.list.pageTexts.filters.startDate')}
                value={filters.startDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                min={filters.startDate || undefined}
                placeholder={t('reconciliation.managerStatement.list.pageTexts.filters.endDate')}
                value={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            {showManagerFilter && (
              <div className="col-md-3 col-sm-4">
                <Dropdown
                  values={filters.managedBy ? [filters.managedBy] : []}
                  options={(managers || []).map((manager: any) => ({
                    label: manager.name,
                    value: String(manager.id),
                  }))}
                  allOptions={(allManagers || []).map((manager: any) => ({
                    label: manager.name,
                    value: String(manager.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.manager}
                  name={VALIDATION_FIELD_MAPPING.managedBy}
                  searchText={dropdownSearchData.managedBy}
                  onSearch={onDropdownSearchChange}
                  placeholder={t("reconciliation.managerStatement.list.pageTexts.filters.selectManager")}
                  label={t("reconciliation.managerStatement.list.pageTexts.filters.manager")}
                  onChange={handleInputChange}
                />
              </div>
            )}

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.status ? [filters.status] : []}
                options={(statuses || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allStatuses || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.statementStatus}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.managerStatement.list.pageTexts.filters.selectStatus")}
                label={t("reconciliation.managerStatement.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("reconciliation.managerStatement.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
